/**
 * product-gallery-tap-zoom.js
 * ---------------------------------------------------------------------------
 * Product gallery enhancements, matching the Toteme full-screen UX:
 *
 *   1. FULL-WIDTH OPEN  — the full-screen lightbox image fills the viewport
 *      WIDTH (instead of PhotoSwipe's default "fit", which leaves side margins
 *      on tall product images).
 *
 *   2. TOP ALIGNED      — when a full-width image is taller than the screen it
 *      opens aligned to the TOP and you scroll down through it (instead of
 *      opening vertically centred).
 *
 *   3. TAP TO OPEN      — a single tap on a product image opens the full-screen
 *      view on touch / small screens. (Desktop already opens it on click
 *      natively, so this only runs below the theme's "md" breakpoint.)
 *
 *   4. VIDEO AUTOPLAY   — autoplays product videos when they scroll into view.
 *      The theme only autoplays the *selected* carousel slide, so in a grid
 *      ("grid_2x") desktop layout a video that isn't the first media never
 *      plays. This plays/pauses native videos based on visibility instead.
 *
 * Everything hooks into the theme's existing <product-gallery> element and its
 * PhotoSwipe instance, so indexing, variant-filtered images, etc. are
 * unaffected. Self-contained / update-safe: no edits to the core bundle.
 */
(function () {
  if (window.__ducktailGalleryEnhance) return;
  window.__ducktailGalleryEnhance = true;

  /* ===================================================================== *
   * Shared helpers
   * ===================================================================== */

  // Zoom level that makes the image exactly as wide as the viewport.
  function widthFill(zoom) {
    var level = (zoom && zoom.panAreaSize && zoom.elementSize)
      ? zoom.panAreaSize.x / zoom.elementSize.x
      : NaN;
    if (isFinite(level) && level > 0) return level;
    return zoom && typeof zoom.fit === 'number' ? zoom.fit : 1; // safe fallback
  }

  // Mirror the theme's own "md" breakpoint (desktop = >= 1000px).
  function isDesktop() {
    try {
      var mq = window.themeVariables &&
               window.themeVariables.mediaQueries &&
               window.themeVariables.mediaQueries.md;
      return window.matchMedia(mq || '(min-width: 1000px)').matches;
    } catch (e) {
      return window.matchMedia('(min-width: 1000px)').matches;
    }
  }

  /* ===================================================================== *
   * 1 + 2 + 3 — full-screen lightbox: full width, top aligned, tap to open
   * ===================================================================== */

  var MOVE_THRESHOLD = 10; // px of movement that counts as a swipe, not a tap
  var startX = 0;
  var startY = 0;
  var moved = false;
  var enhanced = new WeakSet();

  // Configure a gallery's PhotoSwipe instance. Runs once per gallery, BEFORE
  // the lightbox opens, so the instance shared by the desktop click handler and
  // the mobile tap handler picks it up.
  function enhanceLightbox(gallery) {
    if (!gallery || enhanced.has(gallery)) return;
    if (!gallery.hasAttribute('allow-zoom')) return;

    var lb;
    try { lb = gallery.lightBox; } catch (e) { return; } // public getter
    if (!lb || !lb.options) return;

    enhanced.add(gallery);

    var baseMax = parseInt(lb.options.maxZoomLevel, 10);
    if (!isFinite(baseMax) || baseMax < 1) baseMax = 3;

    // Open at full width, no zoom jump on click. Pinch / wheel can still zoom.
    lb.options.initialZoomLevel = widthFill;
    lb.options.secondaryZoomLevel = widthFill;
    lb.options.maxZoomLevel = function (zoom) {
      return Math.max(baseMax, widthFill(zoom) * 1.5);
    };

    // Align the image to the top on open (fires once per slide, right before
    // the open animation, so there is no visible jump). For images that fit
    // vertically both bounds equal the centre, so this is a no-op.
    if (typeof lb.on === 'function') {
      lb.on('firstZoomPan', function (event) {
        var slide = event && event.slide;
        if (!slide || !slide.bounds || !slide.pan) return;
        slide.pan.y = Math.max(slide.bounds.max.y, slide.bounds.min.y); // top
        if (typeof slide.applyCurrentZoomPan === 'function') {
          slide.applyCurrentZoomPan();
        }
      });
    }
  }

  function primeFromEvent(event) {
    var gallery = event.target && event.target.closest
      ? event.target.closest('product-gallery[allow-zoom]')
      : null;
    if (gallery) enhanceLightbox(gallery);
  }

  document.addEventListener('pointerenter', primeFromEvent, true);
  document.addEventListener('pointerdown', function (event) {
    moved = false;
    startX = event.clientX;
    startY = event.clientY;
    primeFromEvent(event);
  }, true);

  document.addEventListener('pointermove', function (event) {
    if (Math.abs(event.clientX - startX) > MOVE_THRESHOLD ||
        Math.abs(event.clientY - startY) > MOVE_THRESHOLD) {
      moved = true;
    }
  }, true);

  document.addEventListener('click', function (event) {
    if (isDesktop()) return; // desktop opens the lightbox on click natively
    if (moved) return;       // it was a swipe / scroll, not a tap

    var img = event.target;
    if (!img || img.tagName !== 'IMG') return;

    var gallery = img.closest('product-gallery');
    if (!gallery || !gallery.hasAttribute('allow-zoom')) return;

    var media = img.closest('.product-gallery__media');
    if (!media || media.getAttribute('data-media-type') !== 'image') return;

    var carousel = gallery.querySelector('.product-gallery__carousel');
    if (!carousel) return;

    enhanceLightbox(gallery);

    var cells = carousel.cells;
    if (!cells || !cells.filter) {
      cells = Array.prototype.slice.call(carousel.children).filter(function (cell) {
        return cell.classList &&
               cell.classList.contains('product-gallery__media') &&
               !cell.hasAttribute('hidden');
      });
    }

    var imageCells = cells.filter(function (cell) {
      return cell.getAttribute('data-media-type') === 'image';
    });

    var index = imageCells.indexOf(media);
    if (index < 0) return;

    gallery.dispatchEvent(new CustomEvent('lightbox:open', {
      bubbles: true,
      detail: { index: index }
    }));
  }, false);

  /* ===================================================================== *
   * 4 — autoplay native product videos when they are in view
   * ===================================================================== */

  var videoObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var player = entry.target.querySelector('video-media');
      if (!player) return;
      if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
        if (typeof player.play === 'function') player.play();
      } else if (typeof player.pause === 'function') {
        player.pause();
      }
    });
  }, { threshold: [0, 0.5, 1] });

  function scanVideos() {
    var cells = document.querySelectorAll(
      'product-gallery[autoplay-media] .product-gallery__media[data-media-type="video"]'
    );
    Array.prototype.forEach.call(cells, function (cell) {
      if (cell.__ducktailVideoObserved) return;
      cell.__ducktailVideoObserved = true;
      videoObserver.observe(cell);
    });
  }

  // Re-scan when the gallery DOM changes (variant change re-render, quick-buy
  // drawer, theme editor), debounced.
  var scanScheduled = false;
  function scheduleScan() {
    if (scanScheduled) return;
    scanScheduled = true;
    (window.requestAnimationFrame || window.setTimeout)(function () {
      scanScheduled = false;
      scanVideos();
    }, 0);
  }

  function init() {
    scanVideos();
    if (document.body) {
      new MutationObserver(scheduleScan).observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();